import ReusableButton from '@app/drive/ui/button';
import { FormattedDate } from '@app/format-date';
import { getStatusDisplayText, getStatusBadgeClasses, fetchDocumentStatus } from '../../services/signature-api';
import { useEffect, useState } from 'react';

interface DocTableProps {
    walletAddress: any;
    email: any;
    allDigitalSignDocs: any[];
    setRowData: (data: any) => void;
    partyId?: string | null;
}

export function DocTable({
    walletAddress,
    email,
    allDigitalSignDocs,
    setRowData,
    partyId,
}: DocTableProps) {
    const [documentStatuses, setDocumentStatuses] = useState<Record<string, string>>({});

    // Fetch document statuses from API when documents change
    useEffect(() => {
        const fetchStatuses = async () => {
            if (!allDigitalSignDocs || allDigitalSignDocs.length === 0) return;

            const statusPromises = allDigitalSignDocs.map(async (doc: any) => {
                try {
                    const documentId = doc.document_id;
                    if (!documentId) return null;

                    const response = await fetchDocumentStatus(documentId);
                    return {
                        documentId,
                        status: response.data.document_status
                    };
                } catch (error) {
                    console.warn(`Failed to fetch status for document ${doc.document_id}:`, error);
                    return null;
                }
            });

            const results = await Promise.all(statusPromises);
            const statusMap: Record<string, string> = {};

            results.forEach(result => {
                if (result) {
                    statusMap[result.documentId] = result.status;
                }
            });

            setDocumentStatuses(statusMap);
        };

        fetchStatuses();
    }, [allDigitalSignDocs]);

    const handleRowClick = (data: any) => {
        console.log('🎯 handleRowClick called with data:', data);
        console.log('🎯 data.document_id:', data?.document_id);
        setRowData(data);
    };

    return (
        <div className="p-16 text-center w-full">
            <h1 className="mb-14 text-[18px] font-bold break-words">
                Listed Documents : {email || walletAddress || partyId}
            </h1>
            <div className="overflow-auto rounded-2xl shadow-lg mb-20">
                <table className="w-full table-auto border-collapse border p-10">
                    <thead className="bg-gray-200">
                        <tr>
                            <th className="border-b px-14 py-10 text-center">#Party ID</th>
                            <th className="border-b px-14 py-10 text-center">#Document Hash Key</th>
                            <th className="border-b px-14 py-10 text-center">Document Type</th>
                            <th className="border-b px-14 py-10 text-center">Author</th>
                            <th className="border-b px-14 py-10 text-center">Sign Authority</th>
                            <th className="border-b px-14 py-10 text-center">Sign Order</th>
                            <th className="border-b px-14 py-10 text-center">Status</th>
                            <th className="border-b px-14 py-10 text-center">Created At</th>
                            <th className="border-b px-14 py-10 text-center">Updated At</th>
                            <th className="border-b px-14 py-10 text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allDigitalSignDocs &&
                            allDigitalSignDocs.length > 0 &&
                            allDigitalSignDocs.map((doc: any, index: number) => {
                                // Get the first party assignment from the array
                                const partyAssignment = doc.party_assignments && doc.party_assignments.length > 0
                                    ? doc.party_assignments[0]
                                    : null;

                                return (
                                    <tr
                                        key={doc.document_id || doc.id}
                                        className={`hover:bg-gray-100 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                            } ${index === allDigitalSignDocs.length - 1
                                                ? 'last:border-b-0'
                                                : ''
                                            }`}
                                    >
                                        <td className="border-b px-14 py-10 text-center">
                                            {partyAssignment?.party_id || partyId || doc.party_id || 'N/A'}
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">
                                            {(doc.docprefix || doc.disk_prefix) ? (
                                                <span className="font-mono text-xs bg-gray-100 px-8 py-4 rounded border border-gray-200">
                                                    {doc.docprefix || doc.disk_prefix}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">N/A</span>
                                            )}
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">
                                            {doc.document_type || 'N/A'}
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">{doc.owner_name || 'N/A'}</td>
                                        <td className="border-b px-14 py-10 text-center">
                                            {partyAssignment?.role_label || 'N/A'}
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">
                                            {partyAssignment?.signing_level || 'N/A'}
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">
                                            {(() => {
                                                // Use the same logic as dashboard: API status first, then party assignment, then doc status
                                                const statusValue = documentStatuses[doc.document_id] || partyAssignment?.status || doc.status;

                                                console.log('🎯 Status Debug for doc:', doc.document_id, {
                                                    apiStatus: documentStatuses[doc.document_id],
                                                    partyAssignmentStatus: partyAssignment?.status,
                                                    docStatus: doc.status,
                                                    finalStatus: statusValue,
                                                    displayText: getStatusDisplayText(statusValue),
                                                    badgeClasses: getStatusBadgeClasses(statusValue)
                                                });

                                                return (
                                                    <span className={`inline-block px-8 py-4 rounded text-xs font-medium ${getStatusBadgeClasses(statusValue)}`}>
                                                        {getStatusDisplayText(statusValue)}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">
                                            <FormattedDate date={doc.created_at} />
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">
                                            <FormattedDate date={doc.processed_at || doc.updated_at} />
                                        </td>
                                        <td className="border-b px-14 py-10 text-center">
                                            <ReusableButton
                                                onClick={() => handleRowClick({
                                                    ...doc,
                                                    partyAssignment,
                                                    partyId,
                                                })}
                                                className="min-w-[100px]"
                                            >
                                                View Doc
                                            </ReusableButton>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
